import type { ConnectionHubError, InboxGroup } from "../../../api/connectionHub.types";
import { INBOX_ACTION } from "../../../api/connectionHub.types";
import { setupStepOf } from "../../../constants/setupSteps.constants";
import { PROVIDER_SETUP_GUIDES } from "../../../constants/providerSetupGuides.constants";
import type { Recheck } from "../../../hooks/useRecheck";
import type { SetupLauncher } from "../../../hooks/useSetupLauncher";
import RecheckPanel from "./RecheckPanel";

export interface InboxRecheckProps {
  group: InboxGroup;
  recheck: Recheck;
  launcher: SetupLauncher;
  error?: ConnectionHubError | string | null;
  disabled?: boolean;
  busy?: boolean;
  onConfirmForwarding: (connectionIds: string[]) => void;
  onConfirmFilter: (connectionIds: string[]) => void;
  onVerify: (connectionIds: string[]) => void;
}

/**
 * The recheck panel bound to an inbox, shared by the hub and the delegate page. Every call is scoped
 * to the failed channels:
 * - forwarding: "I've Set Up" moves to the filter step with no API call; Update runs Gmail's check.
 * - filter / rule: "I've Set Up" verifies straight away (job 14); Update confirms the filter / rule.
 */
const InboxRecheck = ({
  group,
  recheck,
  launcher,
  error,
  disabled = false,
  busy = false,
  onConfirmForwarding,
  onConfirmFilter,
  onVerify,
}: InboxRecheckProps): JSX.Element | null => {
  const { phase, failedIds } = recheck;
  if (!phase) return null;

  const guide = PROVIDER_SETUP_GUIDES[group.inbox.provider];
  const actions = new Set(group.actions);
  const filter = phase === "filter";
  const step = setupStepOf(phase);

  return (
    <RecheckPanel
      phase={phase}
      providerName={guide?.displayName ?? "your inbox"}
      accountEmail={group.inbox.email}
      forwardingAddress={group.forwardingAddress}
      settingsUrl={launcher.urlFor(step)}
      guide={guide}
      rules={group.rules}
      error={error}
      canOpen={actions.has(filter ? INBOX_ACTION.OPEN_FILTER_SETTINGS : INBOX_ACTION.OPEN_FORWARDING_SETTINGS)}
      canCopy={actions.has(INBOX_ACTION.COPY_LINK)}
      canConfirm={actions.has(filter ? INBOX_ACTION.CONFIRM_FILTER : INBOX_ACTION.CONFIRM_FORWARDING)}
      disabled={disabled}
      busy={busy}
      onOpen={() => launcher.open(step)}
      onSkip={() => (phase === "forwarding" ? recheck.skipForwarding() : onVerify(failedIds))}
      onConfirmUpdated={() => (filter ? onConfirmFilter(failedIds) : onConfirmForwarding(failedIds))}
    />
  );
};

export default InboxRecheck;
